import { call } from "redux-saga/effects";
import axios from "axios";

export interface DataPage {
  current: number;
  last: number;
  next: number | null;
  size: number;
  total: number;
}

// The response headers a caller may read, by name. Never the whole header bag: a result is spread
// into REQUEST_ERROR, whose payload the store keeps, so only a header something reads belongs here.
export interface ResponseHeaders {
  retryAfter?: string;
}

export interface BaseRequestResult {
  success: boolean;
  statusCode: number;
  type: string;
  payload: any;
  page?: DataPage | null;
  headers?: ResponseHeaders;
  error?: any;
}

// axios lowercases every response header name; a cross-origin reply only carries the ones the
// API lists in Access-Control-Expose-Headers.
const pickResponseHeaders = (headers: unknown): ResponseHeaders => {
  const retryAfter = (headers as Record<string, unknown> | null | undefined)?.["retry-after"];
  return typeof retryAfter === "string" ? { retryAfter } : {};
};

// Only the delay-seconds form is read, because that is the form the backend sends. An HTTP-date is
// legal too, but it is ignored rather than parsed, so a wait never hangs on this machine's clock.
const DELAY_SECONDS_PATTERN = /^\d+$/;

export const retryAfterSeconds = (headers: ResponseHeaders | null | undefined): number | null => {
  const value = headers?.retryAfter?.trim() ?? "";
  if (!DELAY_SECONDS_PATTERN.test(value)) return null;

  return Number(value);
};

export const baseRequest = async (options: any): Promise<BaseRequestResult> => {
  try {
    const accessToken = options.accessToken;
    if (accessToken) {
      delete options.accessToken;
      options.headers = { Authorization: `Bearer ${accessToken}` };
    }
    // Required so the browser stores/sends the HttpOnly refresh_token cookie
    // across subdomains (app.* -> api.*) and local cross-origin development.
    const response = await axios.request({
      ...options,
      withCredentials: true,
    });
    const statusCode = response.status;
    const success = statusCode >= 200 && statusCode < 300;
    const type = response.data?.type ?? "NONE";
    const payload = response.data?.payload ?? null;
    const page = response.data?.page ?? null;
    const headers = pickResponseHeaders(response.headers);

    return {
      success,
      statusCode,
      type,
      payload,
      page,
      headers,
      error: null,
    };
  } catch (error: any) {
    const success = false;
    const response = error?.response || null;
    const statusCode = response?.status || null;
    const type = response?.data?.type ?? "NONE";
    const payload = response?.data?.payload ?? null;
    const page = response?.data?.page ?? null;
    const headers = pickResponseHeaders(response?.headers);

    error.success = success;
    error.statusCode = statusCode;
    error.type = type;
    error.payload = payload;

    delete error.response;
    error.axiosResponse = response;

    return {
      success,
      statusCode,
      type,
      payload,
      page,
      headers,
      error,
    };
  }
};

export const request = (options: any) => call(() => baseRequest(options));
